// Enterprise Benefits - Premium Grid
import React from 'react';
import { Shield, Clock, Users, HeartHandshake, Headphones, FileCheck, Zap, Lock } from 'lucide-react';
import { useScrollReveal } from '@/hooks/use-scroll-reveal';

interface Benefit {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const benefits: Benefit[] = [
  {
    icon: <Users className="w-6 h-6" />,
    title: 'Dedicated Account Manager',
    description: 'Single point of contact for all communications. Direct access, no ticket queues.',
  },
  {
    icon: <Shield className="w-6 h-6" />,
    title: '99.9% Uptime SLA',
    description: 'Guaranteed availability with financial backing. We stake our reputation on reliability.',
  },
  {
    icon: <Lock className="w-6 h-6" />,
    title: 'Security Compliance',
    description: 'SOC 2 Type II, GDPR, and HIPAA ready. Your data protection is non-negotiable.',
  },
  {
    icon: <Zap className="w-6 h-6" />,
    title: 'Scalable Architecture',
    description: 'Auto-scaling infrastructure that grows with you. No performance bottlenecks.',
  },
  {
    icon: <Headphones className="w-6 h-6" />,
    title: '24/7 Monitoring',
    description: 'Round-the-clock system monitoring. We catch issues before they impact you.',
  },
  {
    icon: <FileCheck className="w-6 h-6" />,
    title: 'Full Documentation',
    description: 'Complete technical documentation and knowledge transfer. Your team owns everything.',
  },
  {
    icon: <Clock className="w-6 h-6" />,
    title: 'Priority Support',
    description: '< 2 hour response time for critical issues. Your success is our priority.',
  },
  {
    icon: <HeartHandshake className="w-6 h-6" />,
    title: 'Long-term Partnership',
    description: "We're invested in your success. Post-launch optimization and continuous improvement.",
  },
];

const BenefitCard = ({ benefit, index, isVisible }: { benefit: Benefit; index: number; isVisible: boolean }) => {
  return (
    <div 
      className="group relative p-6 rounded-xl border border-white/[0.06] bg-white/[0.01] hover:bg-white/[0.03] hover:border-primary/30 transition-all duration-300 hover:-translate-y-1"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
        transition: `all 0.6s cubic-bezier(0.16, 1, 0.3, 1) ${index * 80}ms`,
      }}
    >
      {/* Subtle glow on hover */}
      <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
      
      {/* Icon */}
      <div className="relative w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-4 group-hover:bg-primary/20 transition-all duration-300">
        <span className="group-hover:scale-110 transition-transform duration-300">{benefit.icon}</span>
      </div>
      
      {/* Content */}
      <h3 className="relative text-base font-semibold text-white mb-2 group-hover:text-primary/90 transition-colors">{benefit.title}</h3>
      <p className="relative text-sm text-white/50 leading-relaxed group-hover:text-white/60 transition-colors">{benefit.description}</p>
    </div>
  );
};

export const EnterpriseBenefits = () => {
  const { isVisible, elementRef } = useScrollReveal({ threshold: 0.1 });
  
  return (
    <section 
      ref={elementRef}
      className="relative py-24 lg:py-32 bg-[#0a0a0a]"
    >
      {/* Subtle background */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'linear-gradient(180deg, transparent 0%, hsl(var(--primary) / 0.02) 50%, transparent 100%)',
        }}
      />
      
      <div className="container-professional relative z-10">
        {/* Header */}
        <div 
          className="text-center mb-16"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        >
          <span className="inline-flex items-center gap-2 text-xs font-semibold text-primary uppercase tracking-wider mb-4">
            <Shield className="w-4 h-4" />
            Enterprise Benefits
          </span>
          <h2 className="text-3xl lg:text-5xl font-bold text-white mb-4">
            Built for{' '}
            <span className="text-primary">Enterprise Standards</span>
          </h2>
          <p className="text-lg text-white/60 max-w-2xl mx-auto">
            Beyond code. We provide the infrastructure, support, and guarantees 
            that enterprise operations demand.
          </p>
        </div>
        
        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {benefits.map((benefit, index) => (
            <BenefitCard 
              key={benefit.title}
              benefit={benefit}
              index={index}
              isVisible={isVisible}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
